<template>
  <div>
    <div id="time-series-chart"></div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import cloneDeep from 'lodash.clonedeep'
import * as d3 from 'd3'

const props = {
  data: {
    type: Array,
    required: true
  },
  prop: {
    type: String,
    required: true
  },
  chamber: {
    type: String,
    required: false
  },
  customToggle: {
    type: Boolean,
    required: false,
    default: false
  }
}

const computed = {
  ...mapGetters({
    screenWidth: 'ux/screenWidth',
    mobilePhone: 'ux/mobilePhone',
    mobile: 'ux/mobile',
    currentDate: 'aggregateModels/currentDate'
  })
}

const methods = {
  ...mapActions({
    setCurrentDate: 'aggregateModels/setCurrentDate'
  }),
  drawChart () {
    this.height = 320

    let maxValue = null
    let minTick = null
    let maxTick = null
    if (['gopWinProbability', 'probabilityOfGopMajority'].includes(this.prop)) {
      maxValue = 100
      minTick = 0
      maxTick = 100
    } else if (this.prop === 'meanGopSeats') {
      if (this.chamber === 'house') {
        maxValue = 435
        minTick = 190
        maxTick = 250
      } else if (this.chamber === 'senate') {
        maxValue = 100
        minTick = 40
        maxTick = 60
      }
    }

    const chart = d3.select('#time-series-chart')
    this.width = chart._groups[0][0].clientWidth - this.margin.left - this.margin.right
    this.height = this.height - this.margin.top - this.margin.bottom

    chart.selectAll('*').remove()

    this.svg = chart.append('svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom)

    const g = this.svg.append('g')
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')

    const x = d3.scaleTime().range([0, this.width])
    const y = d3.scaleLinear().range([this.height, 0])

    const timeParse = (value) => {
      const parseFunc = d3.timeParse('%Y-%m-%d')
      return parseFunc(value)
    }

    let data = cloneDeep(this.data)
    data = data.filter(item => new Date(item.date.replace(/-/g, '/')) <= new Date(this.currentDate.replace(/-/g, '/')))

    data.forEach((d) => {
      d.date = timeParse(d.date)
      d.gopWinProbability = d.gopWinProbability * 100
    })

    if (['meanGopSeats', 'probabilityOfGopMajority'].includes(this.prop)) {
      data.forEach((d) => {
        d.meanGopSeats = (d.meanGopSeats).toFixed(0)
        d.sdGopSeats = (d.sdGopSeats).toFixed(0)
        d.probabilityOfGopMajority = (d.probabilityOfGopMajority * 100).toFixed(2)
      })
    }

    data.sort((a, b) => {
      const dataA = a.date
      const dataB = b.date
      return dataA - dataB
    })

    const repLine = d3.line()
      .x((d) => { return x(d.date) })
      .y((d) => { return y(d[this.prop]) })

    const demLine = d3.line()
      .x((d) => { return x(d.date) })
      .y((d) => { return y(maxValue - d[this.prop]) })

    const xTickValues = ['2018-06-19', '2018-07-01', '2018-08-01', '2018-09-01', '2018-10-01', '2018-11-01']
    const newXTickValues = []
    xTickValues.forEach((date, index) => {
      newXTickValues.push(timeParse(date))
    })
    newXTickValues.push(data[data.length - 1].date)
    newXTickValues.sort((a, b) => {
      return a - b
    })
    let xTicks = []
    let endDateIndex = null
    newXTickValues.forEach((date, index) => {
      const dayFormat = d3.timeFormat('%d')
      if (dayFormat(date) === '01' || index === 0) {
        xTicks.push(date)
      }
      if (!endDateIndex && date === data[data.length - 1].date) {
        endDateIndex = index
      }
    })

    // timeParse(xTickValues[endDateIndex])
    // election day: timeParse('2018-11-06')
    const endDate = timeParse('2018-11-07')

    x.domain([timeParse('2018-06-19'), endDate])
    y.domain([minTick, maxTick])

    function makeXGridlines () {
      return d3.axisBottom(x)
        .ticks(5)
    }

    function makeYGridlines () {
      return d3.axisLeft(y)
        .ticks(5)
    }

    let xTickFormat = null
    if (this.mobilePhone) {
      xTickFormat = d3.timeFormat('%b')
    } else {
      xTickFormat = d3.timeFormat('%B')
    }

    g.append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(d3.axisBottom(x)
        .tickValues(xTicks)
        .tickFormat(xTickFormat)
      )
      .selectAll('text')
      .attr('y', 12)
      .attr('font-size', () => {
        if (this.mobilePhone) {
          return '1.1em'
        } else {
          return '1.3em'
        }
      })
      .attr('font-weight', 700)
      .attr('letter-spacing', () => {
        if (this.mobilePhone) {
          return '0.08em'
        } else {
          return '0.12em'
        }
      })

    let yTicks = null
    if (['gopWinProbability', 'probabilityOfGopMajority'].includes(this.prop)) {
      yTicks = [0, 25, 50, 75, 100]
    } else if (this.prop === 'meanGopSeats') {
      if (this.chamber === 'house') {
        yTicks = [190, 200, 218, 240, 250]
      } else if (this.chamber === 'senate') {
        yTicks = [40, 50, 60]
      }
    }

    g.append('g')
      .call(d3.axisLeft(y)
        .tickValues(yTicks)
      )
      .attr('font-size', '0.8em')
      .attr('font-weight', 600)
      .attr('letter-spacing', '0.1em')

    g.append('g')
      .attr('class', 'grid')
      .call(makeYGridlines()
        .tickSize(-this.width)
        .tickValues(yTicks)
        .tickFormat('')
      )

    // let tickValues = null
    // if (this.mobilePhone) {
    //   tickValues = xTicks
    // } else {
    //   tickValues = data.map(item => item.date)
    // }

    g.append('g')
      .attr('class', 'grid vertical-grid-line')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(makeXGridlines()
        .tickSize(-this.height)
        .tickValues(xTicks)
        .tickFormat(d3.timeFormat('%B %d'))
      )
      .selectAll('text')
      .attr('y', -15)
      .attr('x', -12)
      .attr('display', () => {
        const isIE = /* @cc_on!@ */ false || !!document.documentMode
        const isEdge = !isIE && !!window.StyleMedia
        if (isIE || isEdge) {
          return 'none'
        }
      })
      .attr('font-size', '1.3em')
      .attr('font-weight', 700)
      .attr('letter-spacing', '0.13em')
      .attr('text-anchor', 'end')
      .style('opacity', 0.3)
      .style('text-transform', 'uppercase')
      .style('transform', 'rotate(90deg)')
      .style('display', 'none')

    g.append('line')
      .attr('x1', x(timeParse('2018-11-06')))
      .attr('y1', 0)
      .attr('x2', x(timeParse('2018-11-06')))
      .attr('y2', this.height)
      .style('stroke-width', 1)
      .style('stroke', '#57AEA6')
      .style('fill', 'none')

    g.append('text')
      .attr('x', x(timeParse('2018-11-06')))
      .attr('y', -10)
      .style('text-anchor', 'end')
      .style('font-weight', 600)
      .style('font-size', '0.8em')
      .style('fill', '#57AEA6')
      .text(() => {
        if (this.mobile) {
          return 'Election Day'
        } else {
          return 'Election Day (11/6)'
        }
      })

    g.append('line')
      .attr('x1', x(timeParse('2018-09-05')))
      .attr('y1', 0)
      .attr('x2', x(timeParse('2018-09-05')))
      .attr('y2', this.height)
      .style('stroke-width', 1)
      .style('stroke', '#9E9E9E')
      .style('stroke-dasharray', ('3', '3'))
      .style('fill', 'none')

    g.append('text')
      .attr('x', x(timeParse('2018-09-05')))
      .attr('y', -10)
      .style('text-anchor', 'end')
      .style('font-weight', 600)
      .style('font-size', '0.8em')
      .style('fill', '#9E9E9E')
      .text('Daily Updates')

    if (this.prop === 'meanGopSeats') {
      const repUpper = d3.area()
        .x((d) => { return x(d.date) })
        .y0((d) => { return y(d.meanGopSeats) })
        .y1((d) => {
          return y(parseInt(d.meanGopSeats) + (d.sdGopSeats * 2))
        })

      const repLower = d3.area()
        .x((d) => { return x(d.date) })
        .y0((d) => { return y(d.meanGopSeats) })
        .y1((d) => {
          return y(parseInt(d.meanGopSeats) - (d.sdGopSeats * 2))
        })

      const demUpper = d3.area()
        .x((d) => { return x(d.date) })
        .y0((d) => { return y(maxValue - d.meanGopSeats) })
        .y1((d) => {
          return y(maxValue - (parseInt(d.meanGopSeats) + (d.sdGopSeats * 2)))
        })

      const demLower = d3.area()
        .x((d) => { return x(d.date) })
        .y0((d) => { return y(maxValue - d.meanGopSeats) })
        .y1((d) => {
          return y(maxValue - (parseInt(d.meanGopSeats) - (d.sdGopSeats * 2)))
        })

      g.append('path')
        .datum(data)
        .attr('class', 'area')
        .style('fill', '#FA3138')
        .style('opacity', 0.2)
        .attr('d', repUpper)

      g.append('path')
        .datum(data)
        .attr('class', 'area')
        .style('fill', '#FA3138')
        .style('opacity', 0.2)
        .attr('d', repLower)

      g.append('path')
        .datum(data)
        .attr('class', 'area')
        .style('fill', '#66ABCF')
        .style('opacity', 0.2)
        .attr('d', demUpper)

      g.append('path')
        .datum(data)
        .attr('class', 'area')
        .style('fill', '#66ABCF')
        .style('opacity', 0.2)
        .attr('d', demLower)
    }

    g.append('path')
      .datum(data)
      .attr('class', () => {
        const isIE = /* @cc_on!@ */ false || !!document.documentMode
        const isEdge = !isIE && !!window.StyleMedia
        if (isIE || isEdge) {
          return 'dem-line'
        } else {
          return 'dem-line data-line'
        }
      })
      .attr('stroke', '#66ABCF')
      .attr('stroke-width', 3)
      .attr('opacity', 0.7)
      .attr('fill', 'none')
      .attr('d', demLine)

    g.append('path')
      .datum(data)
      .attr('class', () => {
        const isIE = /* @cc_on!@ */ false || !!document.documentMode
        const isEdge = !isIE && !!window.StyleMedia
        if (isIE || isEdge) {
          return 'dem-line'
        } else {
          return 'dem-line data-line'
        }
      })
      .attr('stroke', '#FA3138')
      .attr('stroke-width', 3)
      .attr('opacity', 0.7)
      .attr('fill', 'none')
      .attr('d', repLine)

    this.scoreboardObj = {
      date: data[data.length - 1].date,
      rep: parseFloat(data[data.length - 1][this.prop]),
      dem: maxValue - data[data.length - 1][this.prop],
      hpdGopSeats: data[data.length - 1].hpdGopSeats,
      seats: parseInt(data[data.length - 1].meanGopSeats),
      maxSeats: this.chamber === 'house' ? 435 : 100,
      majority: parseFloat(data[data.length - 1].probabilityOfGopMajority)
    }

    const bisect = d3.bisector(function (d) { return d.date }).right

    const mappedData = [
      { party: 'R', values: [] },
      { party: 'D', values: [] }
    ]
    data.forEach(item => {
      mappedData[0].values.push({
        date: item.date,
        [this.prop]: item[this.prop]
      })
      mappedData[1].values.push({
        date: item.date,
        [this.prop]: maxValue - item[this.prop]
      })
    })

    const mouseG = this.svg.append('g')
      .attr('class', 'mouse-over-effects')

    mouseG.append('path')
      .attr('class', 'mouse-line')
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')
      .style('stroke', '#57AEA6')
      .style('stroke-width', '1.5px')
      .style('stroke-dasharray', ('3', '3'))
      .style('opacity', '0')

    const repFocus = this.svg.append('g')
      .attr('class', 'focus')
      .style('display', 'none')

    repFocus.append('text')
      .attr('class', 'rep-text')

    const demFocus = this.svg.append('g')
      .attr('class', 'focus')
      .style('display', 'none')

    demFocus.append('text')
      .attr('class', 'dem-text')

    if (!this.mobilePhone) {
      this.svg.append('rect')
        .attr('class', 'overlay')
        .attr('width', this.width)
        .attr('height', this.height)
        .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')
        .style('fill', 'none')
        .style('pointer-events', 'all')
        .on('mouseover', () => {
          repFocus.style('display', null)
          demFocus.style('display', null)
          d3.select('.mouse-line').style('opacity', 1)
        })
        .on('mouseout', () => {
          repFocus.style('display', 'none')
          demFocus.style('display', 'none')
          d3.select('.mouse-line').style('opacity', 0)
          d3.select('.default-line').style('opacity', 1)
          repFocus.attr('transform', 'translate(' + x(data[data.length - 1].date) + ',' + y(data[data.length - 1][self.prop]) + ')')
          let date = new Date('2018/11/06')

          self.scoreboardObj = {
            date: data[data.length - 1],
            rep: parseFloat(data[data.length - 1][self.prop]),
            dem: maxValue - parseFloat(data[data.length - 1][self.prop]),
            hpdGopSeats: data[data.length - 1].hpdGopSeats,
            seats: parseInt(data[data.length - 1].meanGopSeats),
            maxSeats: self.chamber === 'house' ? 435 : 100,
            majority: (parseFloat(data[data.length - 1].probabilityOfGopMajority)).toFixed(2),
            currentDate: date
          }
          self.$emit('scoreboard-update', self.scoreboardObj)
        })
        .on('mousemove', mousemove)
    }

    const self = this

    const today = new Date(this.currentDate.replace(/-/g, '/'))
    if (today < new Date('2018/11/06')) {
      // d3.select('.selected-rep-point')
      //   .attr('cx', () => x(self.scoreboardObj.date))
      //   .attr('cy', () => y(self.scoreboardObj.rep))

      // d3.select('.selected-dem-point')
      //   .attr('cx', () => x(self.scoreboardObj.date))
      //   .attr('cy', () => y(self.scoreboardObj.dem))

      this.svg.append('line')
        .attr('class', 'default-line mouse-line')
        .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')')
        .style('stroke', '#57AEA6')
        .style('stroke-width', '1.5px')
        .style('stroke-dasharray', ('3', '3'))
        .attr('x1', x(today))
        .attr('y1', 0)
        .attr('x2', x(today))
        .attr('y2', this.height)
    }

    this.scoreboardObj.currentDate = today
    this.$emit('scoreboard-update', this.scoreboardObj)

    function mousemove () {
      d3.select('.default-line').style('opacity', 0)

      const mouse = d3.mouse(this)[0]
      d3.select('.mouse-line')
        .attr('d', function () {
          let d = 'M' + mouse + ',' + self.height
          d += ' ' + mouse + ',' + 0
          return d
        })

      const x0 = x.invert(mouse)

      d3.select('.mouse-line').style('opacity', 1)

      if (x0 <= new Date(self.currentDate.replace(/-/g, '/'))) {
        const series = mappedData.map((e) => {
          const i = bisect(e.values, x0, 1)
          const d0 = e.values[i - 1]
          const d1 = e.values[i]
          return x0 - d0.x > d1.x - x0 ? d1 : d0
        })

        series.forEach((item, i) => {
          repFocus.attr('transform', 'translate(' + x(series[0].date) + ',' + y(series[0][self.prop]) + ')')
          const obj = data.find(item => item.date === series[0].date)
          self.scoreboardObj = {
            date: series[0].date,
            rep: parseFloat(series[0][self.prop]),
            dem: series[1][self.prop],
            hpdGopSeats: obj.hpdGopSeats,
            seats: parseInt(obj.meanGopSeats),
            maxSeats: self.chamber === 'house' ? 435 : 100,
            majority: parseFloat(obj.probabilityOfGopMajority),
            currentDate: x0
          }
          self.$emit('scoreboard-update', self.scoreboardObj)
          // repFocus.select('text')
          //   .attr('x', x(series[0].date))
          //   .attr('y', y(series[0][self.prop]))
          //   .text(series[0][self.prop])
          // demFocus.attr('transform', 'translate(' + x(series[1].date) + ',' + y(series[1][self.prop]) + ')')
          // demFocus.select('text')
          //   .attr('x', x(series[0].date))
          //   .attr('y', y(series[0][self.prop]))
          //   .text(series[1][self.prop])
        })
      } else {
        d3.select('.mouse-line').style('opacity', 0)
        d3.select('.default-line').style('opacity', 1)
        repFocus.attr('transform', 'translate(' + x(data[data.length - 1].date) + ',' + y(data[data.length - 1][self.prop]) + ')')
        let date = x0
        if (date > new Date(self.currentDate.replace(/-/g, '/'))) {
          date = new Date(self.currentDate.replace(/-/g, '/'))
        }

        self.scoreboardObj = {
          date: data[data.length - 1],
          rep: parseFloat(data[data.length - 1][self.prop]),
          dem: maxValue - parseFloat(data[data.length - 1][self.prop]),
          hpdGopSeats: data[data.length - 1].hpdGopSeats,
          seats: parseInt(data[data.length - 1].meanGopSeats),
          maxSeats: self.chamber === 'house' ? 435 : 100,
          majority: (parseFloat(data[data.length - 1].probabilityOfGopMajority)).toFixed(2),
          currentDate: date
        }
        self.$emit('scoreboard-update', self.scoreboardObj)
      }

      d3.select('.selected-rep-point')
        .attr('cx', () => x(self.scoreboardObj.date))
        .attr('cy', () => y(self.scoreboardObj.rep))

      d3.select('.selected-dem-point')
        .attr('cx', () => x(self.scoreboardObj.date))
        .attr('cy', () => y(self.scoreboardObj.dem))
    }
  }
}

export default {
  props,
  computed,
  methods,
  data () {
    return {
      wdith: null,
      height: null,
      margin: { top: 30, right: 30, bottom: 30, left: 50 },
      svg: null,
      selectedDate: null,
      scoreboardObj: null
    }
  },
  mounted () {
    if (this.mobile) {
      this.margin = { top: 30, right: 20, bottom: 30, left: 40 }
    }
    this.drawChart()
  },
  watch: {
    screenWidth () {
      if (this.mobile) {
        this.margin = { top: 30, right: 20, bottom: 30, left: 40 }
      }
      this.drawChart()
    },
    currentDate () {
      this.drawChart()
    }
  }
}
</script>

<style>
.data-line {
  stroke-dasharray: 5000;
  stroke-dashoffset: 5000;
  -webkit-animation: dash 5s linear forwards;
          animation: dash 5s linear forwards;
}
@-webkit-keyframes dash {
  to {
    stroke-dashoffset: 0;
  }
}
@keyframes dash {
  to {
    stroke-dashoffset: 0;
  }
}

.grid line {
  stroke: lightgrey;
  stroke-opacity: 0.7;
  shape-rendering: crispEdges;
}

.grid path {
  stroke-width: 0;
}

.time-series-tooltip {
  font-family: 'Roboto';
  position: absolute;
  text-align: center;
  width: 66px;
  height: 32px;
  padding: 5px;
  font-size: 15px;
  font-weight: 500;
  background: #fff;
  border: 1px solid black;
  pointer-events: none;
}

.time-series-tooltip-arrow {
  position: absolute;
  background: #fff;
  border: 1px solid #222;
  bottom: -6px;
  left: 26px;
  -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
          transform: rotate(45deg);
  width: 12px;
  height: 12px;
}

.time-series-tooltip-top {
  width: 50px;
  height: 8px;
  bottom: 0;
  position: absolute;
  background: #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/charts/TimeSeries.vue